/**
 * PWA-only app_config UI-set overrides via URL query (or sessionStorage).
 * Native never reads these — gated by {@link isWebRuntime}.
 *
 * Examples:
 *   ?ui_set=set1
 *   ?main_screen_variant=classic
 *   ?main_screen_variant=banner_guest
 *   ?menu_flow_variant=set2&accounts_variant=set3&dashboard_variant=set2
 *
 * sessionStorage key `pwa_ui_set_overrides` (JSON) is also merged when present.
 */
import { isWebRuntime } from '../../../shared/platform/runtimeTarget';
import type {
  AppConfig,
  DashboardVariant,
  MainScreenVariant,
} from './appConfig';

const DASHBOARD_VARIANTS: DashboardVariant[] = ['set1', 'set2', 'set3'];
const MAIN_SCREEN_VARIANTS: MainScreenVariant[] = ['classic', 'banner_guest', 'hero_dock'];

const QUERY_KEYS = [
  'main_screen_variant',
  'dashboard_variant',
  'calendar_variant',
  'menu_flow_variant',
  'accounts_variant',
] as const;

type UiSetKey = (typeof QUERY_KEYS)[number];

function readSearchParams(): URLSearchParams | null {
  try {
    if (typeof globalThis === 'undefined') {
      return null;
    }
    const loc = (globalThis as { location?: { search?: string } }).location;
    if (!loc?.search) {
      return null;
    }
    return new URLSearchParams(loc.search);
  } catch {
    return null;
  }
}

function readSessionOverrides(): Record<string, unknown> {
  try {
    if (typeof globalThis === 'undefined') {
      return {};
    }
    const storage = (globalThis as { sessionStorage?: Storage }).sessionStorage;
    const raw = storage?.getItem('pwa_ui_set_overrides');
    if (!raw) {
      return {};
    }
    const parsed = JSON.parse(raw) as unknown;
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : {};
  } catch {
    return {};
  }
}

function pickDashboard(value: unknown): DashboardVariant | undefined {
  return typeof value === 'string' && (DASHBOARD_VARIANTS as string[]).includes(value)
    ? (value as DashboardVariant)
    : undefined;
}

function pickMain(value: unknown): MainScreenVariant | undefined {
  return typeof value === 'string' && (MAIN_SCREEN_VARIANTS as string[]).includes(value)
    ? (value as MainScreenVariant)
    : undefined;
}

/** `?ui_set=set1` forces dashboard/accounts/calendar/menu variants together. */
function uiSetBundle(value: unknown): Partial<AppConfig> {
  const set = pickDashboard(value);
  if (!set) {
    return {};
  }
  return {
    dashboard_variant: set,
    calendar_variant: set,
    menu_flow_variant: set,
    accounts_variant: set,
  };
}

/** Merge PWA URL/session UI-set overrides onto a resolved app config. No-op on native. */
export function applyPwaUiSetOverrides(config: AppConfig): AppConfig {
  if (!isWebRuntime()) {
    return config;
  }

  const next: AppConfig = { ...config };
  const from_session = readSessionOverrides();
  const params = readSearchParams();

  const sources: Array<Partial<AppConfig> & Partial<Record<UiSetKey, unknown>>> = [
    from_session as Partial<AppConfig>,
    uiSetBundle(from_session.ui_set),
  ];
  if (params) {
    const from_query: Partial<Record<UiSetKey, string>> = {};
    for (const key of QUERY_KEYS) {
      const v = params.get(key);
      if (v) {
        from_query[key] = v;
      }
    }
    sources.push(uiSetBundle(params.get('ui_set')));
    sources.push(from_query);
  }

  for (const src of sources) {
    const main = pickMain(src.main_screen_variant);
    if (main) {
      next.main_screen_variant = main;
    }
    const dash = pickDashboard(src.dashboard_variant);
    if (dash) {
      next.dashboard_variant = dash;
    }
    const cal = pickDashboard(src.calendar_variant);
    if (cal) {
      next.calendar_variant = cal;
    }
    const menu = pickDashboard(src.menu_flow_variant);
    if (menu) {
      next.menu_flow_variant = menu;
    }
    const accounts = pickDashboard(src.accounts_variant);
    if (accounts) {
      next.accounts_variant = accounts;
    }
  }

  return next;
}
